import * as React from 'react';
import { setSessionId as setTelemetrySessionId } from '@fusion/frontend-analytics';
import { resolveEntraConfig, type EntraRuntimeConfig } from './config';
import { buildDevRoleHeaders, readDevRolePreference } from '@/lib/dev-role-preference';

export interface CockpitPrincipal {
  homeAccountId: string;
  username: string;
  displayName: string;
  roles: readonly CockpitRole[];
}

export type CockpitRole = 'SO-Operator' | 'SO-Supervisor' | 'SO-Admin';

export const ROLE_VALUES: readonly CockpitRole[] = ['SO-Operator', 'SO-Supervisor', 'SO-Admin'];

interface AuthState {
  principal: CockpitPrincipal | null;
  sessionId: string | null;
  acquireToken: () => Promise<string | null>;
  signIn: () => void;
  signOut: () => void;
  config: EntraRuntimeConfig;
  loading: boolean;
}

interface MeResponse {
  principal: CockpitPrincipal;
  sessionId: string;
}

const AuthContext = React.createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const config = React.useMemo(() => resolveEntraConfig(), []);
  const [principal, setPrincipal] = React.useState<CockpitPrincipal | null>(null);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    async function loadPrincipal(): Promise<void> {
      try {
        const devHeaders = buildDevRoleHeaders(readDevRolePreference());
        const response = await fetch('/api/v1/auth/me', {
          credentials: 'include',
          headers: { ...devHeaders },
        });
        if (!response.ok) {
          if (!cancelled) {
            setPrincipal(null);
            setSessionId(null);
          }
          return;
        }
        const data = (await response.json()) as MeResponse;
        if (!cancelled) {
          setPrincipal(data.principal);
          setSessionId(data.sessionId);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadPrincipal();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = React.useCallback(() => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(`/api/v1/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  }, []);

  const signOut = React.useCallback(() => {
    void fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => response.json() as Promise<{ redirectTo?: string }>)
      .then((data) => {
        window.location.assign(data.redirectTo ?? '/signed-out');
      })
      .catch(() => {
        window.location.assign('/signed-out');
      });
  }, []);

  const value = React.useMemo<AuthState>(
    () => ({
      principal,
      sessionId,
      acquireToken: async () => null,
      signIn,
      signOut,
      config,
      loading,
    }),
    [principal, sessionId, signIn, signOut, config, loading],
  );

  React.useEffect(() => {
    setTelemetrySessionId(sessionId ?? '');
  }, [sessionId]);

  React.useEffect(() => {
    if (!loading && !principal && window.location.pathname !== '/signed-out') {
      signIn();
    }
  }, [loading, principal, signIn]);

  const holdChildren = loading || (!principal && window.location.pathname !== '/signed-out');
  return (
    <AuthContext.Provider value={value}>{holdChildren ? null : children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function useHasRole(role: CockpitRole | readonly CockpitRole[]): boolean {
  const { principal } = useAuth();
  if (!principal) return false;
  const allowed = Array.isArray(role) ? role : [role];
  return principal.roles.some((r) => allowed.includes(r));
}

export function useIsSupervisorOrAdmin(): boolean {
  return useHasRole(['SO-Supervisor', 'SO-Admin']);
}

export function useIsAdmin(): boolean {
  return useHasRole('SO-Admin');
}

export function useIsAuthenticated(): boolean {
  return useAuth().principal !== null;
}

export function AuthenticatedTemplate({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null {
  return useIsAuthenticated() ? <>{children}</> : null;
}

export function UnauthenticatedTemplate({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null {
  return useIsAuthenticated() ? null : <>{children}</>;
}
